import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Flex, Text, SimpleGrid, Icon, HStack, VStack, Button, Checkbox, Divider,
    Alert, AlertIcon, AlertDescription, Skeleton, Tooltip, useToast,
} from '@chakra-ui/react';
import {
    FaCoins, FaBalanceScale, FaCheckCircle, FaArchive, FaSyncAlt, FaQuestionCircle, FaMoneyCheckAlt, FaFileExcel,
} from 'react-icons/fa';
import { StatCard, SectionHeader } from '../PainelComissoes';
import { useConfirm } from '../../ConfirmDialog';
import ListaVendasPagamento from './ListaVendasPagamento';
import ListaFechamentos from './ListaFechamentos';
import HistoricoEventos from './HistoricoEventos';
import ModalRegistrarPagamento from './ModalRegistrarPagamento';
import ModalMotivo from './ModalMotivo';
import ModalExportar from './ModalExportar';
import { formatarCentavos, formatarData, mensagemErro } from '../formato';
import {
    getPagamentosComissao, registrarPagamentoComissao, desfazerPagamentoComissao,
    desfazerItemPagamentoComissao, reenviarPagamentoComissaoSite, alterarRastreioComissao,
} from '../../../services/api';

// Aba Pagamentos da Vendas Vendedor (#1518). O vendedor so le (com busca, filtros e
// exportacao); quem gerencia registra, desfaz, reenvia ao site e move vendas entre
// "A pagar" e "Fora da rastreabilidade", uma a uma ou em massa.
const PainelPagamentos = ({ userId }) => {
    const toast = useToast();
    const { confirm, ConfirmPortal } = useConfirm();
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [versao, setVersao] = useState(0);
    const [vendasSel, setVendasSel] = useState(new Set());
    const [compSel, setCompSel] = useState(new Set());
    const [modalPagamento, setModalPagamento] = useState(false);
    const [modalExportar, setModalExportar] = useState(false);
    const [desfazer, setDesfazer] = useState(null);
    const [movendo, setMovendo] = useState(false);

    const carregar = useCallback(async () => {
        if (!userId) return;
        setCarregando(true);
        setErro(null);
        try {
            const r = await getPagamentosComissao(userId);
            setDados(r);
            // Mantem so o que ainda pode ser selecionado.
            const pagaveis = new Set([...r.a_pagar, ...r.fora].map((v) => v.venda_id));
            setVendasSel((atual) => new Set([...atual].filter((id) => pagaveis.has(id))));
            const comps = new Set(r.compensacoes.map((c) => c.item_id));
            setCompSel((atual) => new Set([...atual].filter((id) => comps.has(id))));
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível carregar os pagamentos.'));
        } finally {
            setCarregando(false);
        }
    }, [userId]);

    useEffect(() => {
        setDados(null);
        setVendasSel(new Set());
        setCompSel(new Set());
        carregar();
    }, [carregar]);

    // Depois de qualquer acao: recarrega o resumo e as listas paginadas.
    const atualizarTudo = () => {
        carregar();
        setVersao((v) => v + 1);
    };

    const podeGerenciar = !!dados?.pode_gerenciar;
    const todas = useMemo(() => (dados ? [...dados.a_pagar, ...dados.fora] : []), [dados]);
    const vendasEscolhidas = todas.filter((v) => vendasSel.has(v.venda_id));
    const compEscolhidas = (dados?.compensacoes || []).filter((c) => compSel.has(c.item_id));
    const totalSelecao = vendasEscolhidas.reduce((s, v) => s + v.valor_centavos, 0)
        + compEscolhidas.reduce((s, c) => s + c.sugerido_centavos, 0);
    const temSelecao = vendasEscolhidas.length + compEscolhidas.length > 0;
    const selAPagar = vendasEscolhidas.filter((v) => v.situacao === 'a_pagar');
    const selFora = vendasEscolhidas.filter((v) => v.situacao === 'fora');

    const alternar = (setter) => (id) => setter((atual) => {
        const novo = new Set(atual);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        return novo;
    });
    const marcarVarias = (ids, marcar) => setVendasSel((atual) => {
        const novo = new Set(atual);
        ids.forEach((id) => (marcar ? novo.add(id) : novo.delete(id)));
        return novo;
    });
    const limparSelecao = () => { setVendasSel(new Set()); setCompSel(new Set()); };

    const registrar = async (campos) => {
        try {
            const r = await registrarPagamentoComissao({
                user_id: userId,
                vendas: vendasEscolhidas.map((v) => v.venda_id),
                compensacoes: compEscolhidas.map((c) => c.item_id),
                ...campos,
            });
            const f = r.fechamento;
            toast({
                title: `Pagamento #${f.id} registrado`,
                description: `${formatarCentavos(f.valor_total_centavos)} para ${dados.vendedor.name}.${f.site.total ? ` Site: ${f.site.ok} de ${f.site.total} confirmada(s).` : ''}`,
                status: 'success',
                duration: 6000,
                isClosable: true,
            });
            setModalPagamento(false);
            limparSelecao();
            atualizarTudo();
            return null;
        } catch (e) {
            return mensagemErro(e, 'Não foi possível registrar o pagamento.');
        }
    };

    const confirmarDesfazer = async (motivo) => {
        try {
            if (desfazer.item) {
                await desfazerItemPagamentoComissao(desfazer.item.id, motivo);
            } else {
                await desfazerPagamentoComissao(desfazer.fechamento.id, motivo);
            }
            toast({
                title: desfazer.item ? `Venda #${desfazer.item.venda_id} desfeita do pagamento #${desfazer.fechamento.id}` : `Pagamento #${desfazer.fechamento.id} desfeito`,
                description: 'As vendas voltaram para "A pagar".',
                status: 'success',
                isClosable: true,
            });
            setDesfazer(null);
            atualizarTudo();
            return null;
        } catch (e) {
            return mensagemErro(e, 'Não foi possível desfazer.');
        }
    };

    const reenviar = async (f) => {
        const ok = await confirm({
            title: 'Reenviar ao site',
            body: `Tentar de novo avisar o Sistema de Parceiros do site sobre o pagamento #${f.id}?`,
            confirmText: 'Reenviar',
            confirmColor: 'purple',
        });
        if (!ok) return;
        try {
            const r = await reenviarPagamentoComissaoSite(f.id);
            const s = r.fechamento.site;
            const resolvido = s.pendente === 0 && s.recusado === 0;
            toast({
                title: resolvido ? 'Site atualizado' : 'Reenvio feito, ainda há pendência',
                description: `${s.ok} de ${s.total} confirmada(s) no site.`,
                status: resolvido ? 'success' : 'warning',
                isClosable: true,
            });
            atualizarTudo();
        } catch (e) {
            toast({ title: 'Falha ao reenviar', description: mensagemErro(e, 'Tente de novo em instantes.'), status: 'error', isClosable: true });
        }
    };

    const mover = async (vendas, status) => {
        if (!vendas.length) return;
        const paraAPagar = status === 'a_pagar';
        const soma = vendas.reduce((s, v) => s + v.valor_centavos, 0);
        const qual = vendas.length === 1 ? `A venda #${vendas[0].venda_id}` : `As ${vendas.length} vendas selecionadas`;
        const ok = await confirm({
            title: paraAPagar ? 'Mover para "A pagar"' : 'Mover para "Fora da rastreabilidade"',
            body: `${qual} (${formatarCentavos(soma)}) ${paraAPagar ? 'passa(m) a entrar na conta de pagamento' : 'sai(em) da conta de pagamento'}. Dá para voltar depois.`,
            confirmText: 'Mover',
        });
        if (!ok) return;
        setMovendo(true);
        try {
            await alterarRastreioComissao({ user_id: userId, vendas: vendas.map((v) => v.venda_id), status });
            toast({ title: vendas.length === 1 ? `Venda #${vendas[0].venda_id} movida` : `${vendas.length} vendas movidas`, status: 'success', duration: 2500, isClosable: true });
            marcarVarias(vendas.map((v) => v.venda_id), false);
            atualizarTudo();
        } catch (e) {
            toast({ title: 'Não foi possível mover', description: mensagemErro(e, 'Nada foi alterado.'), status: 'error', isClosable: true });
        } finally {
            setMovendo(false);
        }
    };

    if (!userId) return null;

    if (erro && !dados) {
        return (
            <Box maxW={{ base: '100%', md: '92%', lg: '85%' }} mx="auto" px={{ base: 3, md: 6 }}>
                <Alert status="error" borderRadius="xl" flexWrap="wrap" gap={2}>
                    <AlertIcon />
                    <AlertDescription flex="1">{erro}</AlertDescription>
                    <Button size="sm" onClick={carregar}>Tentar de novo</Button>
                </Alert>
            </Box>
        );
    }

    const totais = dados?.totais;

    return (
        <Box maxW={{ base: '100%', md: '92%', lg: '85%' }} mx="auto" px={{ base: 3, md: 6 }} pb={temSelecao ? 28 : 0}>
            <Flex bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" boxShadow="sm" p={{ base: 3, md: 4 }} mb={4} gap={3} align={{ base: 'stretch', md: 'center' }} direction={{ base: 'column', md: 'row' }}>
                <HStack spacing={3} flex="1" align="flex-start">
                    <Icon as={FaMoneyCheckAlt} color="#F68633" boxSize={5} mt={0.5} />
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">Pagamento de comissões</Text>
                        <Text fontSize="xs" color="gray.500">
                            A conta começa nas vendas de {formatarData(dados?.inicio_rastreio || '2026-11-01')}.
                            {podeGerenciar ? ' Selecione as vendas e registre o pagamento.' : ' Aqui você acompanha o que já foi pago e o que está a pagar.'}
                        </Text>
                    </Box>
                </HStack>
                <HStack spacing={2}>
                    <Button size="sm" leftIcon={<Icon as={FaFileExcel} />} variant="outline" colorScheme="green" onClick={() => setModalExportar(true)} flex={{ base: 1, md: 'none' }}>
                        Exportar
                    </Button>
                    <Button size="sm" leftIcon={<Icon as={FaSyncAlt} />} variant="outline" onClick={atualizarTudo} isLoading={carregando} loadingText="Atualizando" flex={{ base: 1, md: 'none' }}>
                        Atualizar
                    </Button>
                </HStack>
            </Flex>

            {erro && dados && (
                <Alert status="error" borderRadius="md" mb={4} fontSize="sm"><AlertIcon />{erro}</Alert>
            )}

            <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4} mb={5}>
                <StatCard icon={FaCoins} label="A pagar" value={totais ? `${formatarCentavos(totais.a_pagar_centavos)} (${totais.a_pagar_qtd})` : ''} accent="#F68633" loading={!dados} />
                <StatCard icon={FaBalanceScale} label="Compensações sugeridas" labelLines={2} value={totais ? `${formatarCentavos(totais.compensacoes_centavos)} (${totais.compensacoes_qtd})` : ''} accent="#805AD5" loading={!dados} />
                <StatCard icon={FaCheckCircle} label={`Pago em ${totais?.ano || ''}`} value={totais ? formatarCentavos(totais.pago_no_ano_centavos) : ''} accent="#38A169" loading={!dados} />
                <StatCard icon={FaArchive} label="Fora da rastreabilidade" labelLines={2} value={totais ? `${totais.fora_qtd} venda(s)` : ''} accent="#718096" loading={!dados} />
            </SimpleGrid>

            {!dados ? (
                <VStack spacing={3} mb={5}>
                    {[0, 1, 2].map((i) => <Skeleton key={i} h="64px" w="100%" borderRadius="xl" />)}
                </VStack>
            ) : (
                <>
                    <SectionHeader>A pagar</SectionHeader>
                    <ListaVendasPagamento
                        key={`a-pagar-${userId}`}
                        titulo="Vendas a pagar"
                        ajuda="Comissões apuradas desde 01/11/2026 e ainda não pagas. O valor é o da aba Comissões."
                        vendas={dados.a_pagar}
                        podeGerenciar={podeGerenciar}
                        selecionadas={vendasSel}
                        onAlternar={alternar(setVendasSel)}
                        onMarcar={marcarVarias}
                        acaoLinha={(v) => ({ label: 'Mover para fora', onClick: () => mover([v], 'fora') })}
                        textoVazio="Nada a pagar para este vendedor."
                        idPrefixo="a-pagar"
                    />

                    {dados.compensacoes.length > 0 && (
                        <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="purple.200" boxShadow="sm" overflow="hidden" mb={5}>
                            <Flex px={{ base: 3, md: 4 }} py={3} align="center" gap={2} borderBottomWidth="1px" borderBottomColor="gray.100">
                                <Text fontWeight="semibold" color="gray.800" fontSize="sm">Compensações sugeridas ({dados.compensacoes.length})</Text>
                                <Tooltip label="Venda que mudou depois de paga (cancelada, item cancelado ou devolvido). A diferença é descontada no próximo pagamento, se o administrador confirmar (Cláusula 4.4)." hasArrow fontSize="xs" maxW="320px">
                                    <span tabIndex={0} aria-label="O que é compensação"><Icon as={FaQuestionCircle} color="gray.400" boxSize={3.5} cursor="help" /></span>
                                </Tooltip>
                            </Flex>
                            <VStack align="stretch" spacing={0} divider={<Divider />}>
                                {dados.compensacoes.map((c) => (
                                    <Flex key={c.item_id} px={{ base: 3, md: 4 }} py={3} gap={3} align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} bg={compSel.has(c.item_id) ? 'purple.50' : 'white'}>
                                        <Flex gap={3} align="center" flex="1" minW={0}>
                                            {podeGerenciar && (
                                                <Checkbox colorScheme="purple" isChecked={compSel.has(c.item_id)} onChange={() => alternar(setCompSel)(c.item_id)} aria-label={`Selecionar compensação da venda ${c.venda_id}`} />
                                            )}
                                            <Box minW={0}>
                                                <HStack spacing={2} flexWrap="wrap">
                                                    <Text fontWeight="bold" fontSize="sm">#{c.venda_id}</Text>
                                                    {c.id_origem && <Text fontSize="xs" color="gray.500">{c.id_origem}</Text>}
                                                </HStack>
                                                <Text fontSize="xs" color="gray.600">{c.motivo}</Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    Paga em {formatarData(c.pago_em)}: {formatarCentavos(c.pago_centavos)} · hoje vale {formatarCentavos(c.atual_centavos)}
                                                    {c.ja_compensado_centavos ? ` · já compensado ${formatarCentavos(c.ja_compensado_centavos)}` : ''}
                                                </Text>
                                            </Box>
                                        </Flex>
                                        <Text fontWeight="bold" color={c.sugerido_centavos < 0 ? 'red.600' : 'green.600'} alignSelf={{ base: 'flex-end', md: 'center' }}>
                                            {formatarCentavos(c.sugerido_centavos)}
                                        </Text>
                                    </Flex>
                                ))}
                            </VStack>
                        </Box>
                    )}

                    <SectionHeader>Pagamentos registrados</SectionHeader>
                    <ListaFechamentos
                        key={userId}
                        userId={userId}
                        versao={versao}
                        podeGerenciar={podeGerenciar}
                        onDesfazer={(fe) => setDesfazer({ fechamento: fe })}
                        onDesfazerItem={(fe, item) => setDesfazer({ fechamento: fe, item })}
                        onReenviar={reenviar}
                    />

                    <SectionHeader>Fora da rastreabilidade</SectionHeader>
                    <ListaVendasPagamento
                        key={`fora-${userId}`}
                        titulo="Vendas fora da rastreabilidade"
                        ajuda="Vendas de antes de 01/11/2026, quando o controle de pagamento começou. Não entram na conta: o administrador pode marcar como paga (selecionando) ou mover para A pagar."
                        vendas={dados.fora}
                        podeGerenciar={podeGerenciar}
                        selecionadas={vendasSel}
                        onAlternar={alternar(setVendasSel)}
                        onMarcar={marcarVarias}
                        acaoLinha={(v) => ({ label: 'Mover para A pagar', onClick: () => mover([v], 'a_pagar') })}
                        textoVazio="Nenhuma venda fora da rastreabilidade."
                        recolhivel
                        abertaInicial={false}
                        idPrefixo="fora"
                    />
                    {dados.fora_truncado && <Text fontSize="xs" color="gray.500" mt={-3} mb={5}>Mostrando as 500 vendas mais recentes fora da rastreabilidade.</Text>}

                    <HistoricoEventos key={userId} userId={userId} versao={versao} />
                </>
            )}

            {podeGerenciar && temSelecao && (
                // No celular fica acima do botao flutuante de voltar ao topo.
                <Box position="sticky" bottom={{ base: 20, md: 4 }} zIndex={10}>
                    <Flex
                        bg="gray.800"
                        color="white"
                        borderRadius="xl"
                        boxShadow="xl"
                        px={{ base: 3, md: 5 }}
                        py={3}
                        gap={3}
                        align="center"
                        justify="space-between"
                        flexWrap="wrap"
                        role="region"
                        aria-label="Seleção para pagamento"
                    >
                        <Box>
                            <Text fontSize="sm" fontWeight="semibold">
                                {vendasEscolhidas.length} venda(s){compEscolhidas.length ? ` + ${compEscolhidas.length} compensação(ões)` : ''}
                            </Text>
                            <Text fontSize="lg" fontWeight="bold" color={totalSelecao > 0 ? 'orange.200' : 'red.200'}>{formatarCentavos(totalSelecao)}</Text>
                        </Box>
                        <Flex gap={2} flexWrap="wrap" justify="flex-end">
                            <Button size="sm" variant="ghost" color="white" _hover={{ bg: 'whiteAlpha.200' }} onClick={limparSelecao}>Limpar</Button>
                            {selAPagar.length > 0 && (
                                <Button size="sm" variant="outline" color="white" borderColor="whiteAlpha.500" _hover={{ bg: 'whiteAlpha.200' }} onClick={() => mover(selAPagar, 'fora')} isLoading={movendo}>
                                    Mover para fora ({selAPagar.length})
                                </Button>
                            )}
                            {selFora.length > 0 && (
                                <Button size="sm" variant="outline" color="white" borderColor="whiteAlpha.500" _hover={{ bg: 'whiteAlpha.200' }} onClick={() => mover(selFora, 'a_pagar')} isLoading={movendo}>
                                    Mover para A pagar ({selFora.length})
                                </Button>
                            )}
                            <Button size="sm" colorScheme="orange" onClick={() => setModalPagamento(true)} isDisabled={totalSelecao <= 0}>
                                Registrar pagamento
                            </Button>
                        </Flex>
                    </Flex>
                </Box>
            )}

            <ModalRegistrarPagamento
                isOpen={modalPagamento}
                onClose={() => setModalPagamento(false)}
                vendedor={dados?.vendedor}
                vendas={vendasEscolhidas}
                compensacoes={compEscolhidas}
                onConfirmar={registrar}
            />

            <ModalMotivo
                isOpen={!!desfazer}
                onClose={() => setDesfazer(null)}
                titulo={desfazer?.item ? `Desfazer a venda #${desfazer.item.venda_id} do pagamento #${desfazer.fechamento.id}` : `Desfazer o pagamento #${desfazer?.fechamento?.id}`}
                descricao="Use para corrigir uma marcação feita por engano. As vendas voltam para A pagar e o site é avisado. O registro do pagamento continua no histórico."
                resumo={desfazer && (
                    <Text fontSize="sm" fontWeight="semibold">
                        Valor: {formatarCentavos(desfazer.item ? desfazer.item.valor_centavos : desfazer.fechamento.valor_ativo_centavos)}
                    </Text>
                )}
                confirmText="Desfazer"
                onConfirmar={confirmarDesfazer}
            />

            <ModalExportar isOpen={modalExportar} onClose={() => setModalExportar(false)} userId={userId} vendedor={dados?.vendedor} />

            <ConfirmPortal />
        </Box>
    );
};

export default PainelPagamentos;
